import { t } from "@lingui/macro";
import {
  type ProjectForNavigationQuery,
  ProjectTypeEnum,
  useProjectForNavigationQuery,
} from "@src/__generated__/graphql";
import { useNavigationPages } from "@src/components/widgets/AppSubNav/hooks";
import type { NavigationHook } from "@src/components/widgets/AppSubNav/hooks/types";
import { useStore } from "@src/utils/hooks";

export const useProjectNavigationPages = (
  projectId: string | undefined,
): NavigationHook<ProjectForNavigationQuery["project"]> => {
  const { workspaceStore, authStore } = useStore();

  const { data, loading } = useProjectForNavigationQuery({
    variables: { id: projectId ?? "" },
    skip: !projectId,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "cache-and-network",
  });

  const { allPages, activePage, activeSubpage, visiblePages } =
    useNavigationPages(
      [
        {
          href: { pathname: "/projects/detail", query: { id: projectId } },
          title: () => t`Overview`,
        },
        {
          href: { pathname: "/projects/tasks", query: { id: projectId } },
          title: () => t`Tasks`,
          hidden: !authStore.isInternalUser,
        },
        {
          href: { pathname: "/projects/budgets", query: { id: projectId } },
          title: () => t`Budgets`,
          hidden:
            !data?.project?.uses_budgetovac ||
            data?.project?.type === ProjectTypeEnum.TimeMaterials ||
            !authStore.isInternalUser,
          subPages: [
            {
              isDisabled: true,
              href: {
                pathname: "/projects/budgets/create",
                query: { id: projectId },
              },
              title: () => t`Create`,
            },
            {
              isDisabled: true,
              href: {
                pathname: "/projects/budgets/edit",
                query: { id: projectId },
              },
              title: () => t`Edit`,
            },
          ],
        },
        {
          href: { pathname: "/projects/budget", query: { id: projectId } },
          title: () => t`Budget`,
          hidden:
            data?.project?.uses_budgetovac ||
            data?.project?.type === ProjectTypeEnum.TimeMaterials ||
            !authStore.isInternalUser,
        },
        {
          href: { pathname: "/projects/documents", query: { id: projectId } },
          title: () => t`Documents`,
          hidden: !authStore.isInternalUser,
        },
        {
          href: {
            pathname: "/projects/invoicing-plan",
            query: { id: projectId },
          },
          title: () => t`Invoicing plan`,
          hidden:
            !workspaceStore.settings?.moduleSettings?.billing_plan ||
            !authStore.isInternalUser,
        },
        {
          href: { pathname: "/projects/time-report", query: { id: projectId } },
          title: () => t`Time report`,
          hidden: data?.project?.is_template || !authStore.isInternalUser,
        },
        {
          href: {
            pathname: "/projects/time-billing",
            query: { id: projectId },
          },
          title: () => t`Time Billing`,
          hidden:
            !workspaceStore.settings?.moduleSettings?.time_billing ||
            !authStore.isInternalUser,
        },
        {
          href: { pathname: "/projects/settings", query: { id: projectId } },
          title: () => t`Settings`,
          hidden:
            !workspaceStore.settings?.moduleSettings?.time_tracking ||
            !authStore.isInternalUser,
        },
        {
          href: { pathname: "/projects/edit", query: { id: projectId } },
          title: () => t`Edit`,
          hidden: true,
        },
        {
          href: { pathname: "/projects/create-from-template" },
          title: () => t`New project from template`,
          hidden: true,
        },
        {
          href: { pathname: "/projects/create-template" },
          title: () => t`New project template`,
          hidden: true,
        },
        {
          href: { pathname: "/projects/create" },
          title: () => t`New project`,
          hidden: true,
        },
      ],
      "allPages",
    );

  return {
    data: data?.project,
    isLoading: loading,
    allPages,
    activePage,
    activeSubpage,
    visiblePages,
  };
};
